/* eslint-disable no-restricted-imports */
import React, {useMemo, useRef} from 'react'
import {UIGameActionButton} from '../../../common/UIGameActionButton/index.ts'
import {
    UIGameResourceCardsTooltipContent,
    type UIGameResourceCardsTooltipContentHandle,
} from '../../../common/UIGameResourceCardsTooltipContent/index.ts'
import {getCardsForPiece} from '@colonist/game-card-types'
import {PieceTypes} from '@colonist/game-base-types'
import {useActionButtonCommonErrors} from '../utils.ts'
import {SHIP_IMAGE_PATH} from './utils.ts'
import {useGameStore} from '../../../store/GameStoreContext.ts'
import {usePlayerValidators} from '../../../store/selectors/usePlayerValidators.ts'
import {ActionState} from '@colonist/game-states'
import {type TranslationObject} from '@colonist/i18n'
import {useUIGameSocketContext} from '../../../contexts/UIGameSocketContext.tsx'
import {useCurrentStateValidator} from '../../../store/selectors/useCurrentStateValidator.ts'
import {useMechanicValidatorShip} from '../../../store/selectors/useMechanicValidatorShip.ts'

const cardsToBuildShip = getCardsForPiece(PieceTypes.Ship)

export const UIGameActionButtonShip = ({
    className,
}: {
    className?: string
}) => {
    const myColor = useGameStore((state) => state.myColor)
    const currentStateValidator = useCurrentStateValidator()
    const {getPlayerValidator} = usePlayerValidators()
    const {sendBuildShip, sendCancelAction, sendClearRoadEdgeHighlight} = useUIGameSocketContext()
    const playerValidator = getPlayerValidator(myColor)
    const {errorHelperText, hasCommonErrors} = useActionButtonCommonErrors()
    const tooltipRef = useRef<UIGameResourceCardsTooltipContentHandle>(null)
    const shipValidator = useMechanicValidatorShip()

    const actionStatus = useMemo(() => {
        if(!shipValidator) {
            return {canTakeAction: false}
        }

        const useResources = true
        const canPlaceShipActionStatus = shipValidator.canPlayerPlaceShip(myColor, useResources)

        return canPlaceShipActionStatus
    }, [shipValidator, myColor])

    const triggerFlickerCardsAnimation = () => {
        const notEnoughResources = !playerValidator.hasResourceCardsInHand(cardsToBuildShip)
        if(notEnoughResources) {
            tooltipRef.current?.triggerFlickerAnimation()
        }
    }

    const disabled = hasCommonErrors || !actionStatus.canTakeAction
    const actionState = currentStateValidator.getPlayerActionState(myColor)
    const isPlacingShip = actionState === ActionState.PlaceShip && !disabled
    const bankShipAmount = shipValidator?.getShipPlayerState(myColor).bankShipAmount ?? 0
    const icon = SHIP_IMAGE_PATH[myColor]
    const helperText: TranslationObject | undefined = errorHelperText || actionStatus.errorMessageIfCantTakeAction

    const onClick = () => {
        if(disabled) return
        if(isPlacingShip) {
            sendClearRoadEdgeHighlight()
            sendCancelAction()
        } else {
            sendBuildShip()
        }
    }

    return <UIGameActionButton
        className={className}
        count={bankShipAmount}
        countColor={myColor}
        disabled={disabled}
        helperText={helperText}
        highlight={isPlacingShip}
        iconPath={icon}
        onClick={onClick}
        onClickActionIfDisabled={triggerFlickerCardsAnimation}
        tooltipCustomMenuComponent={
            <UIGameResourceCardsTooltipContent
                cardInventory={playerValidator.resourceCards.cards}
                cardsToBuild={cardsToBuildShip}
                ref={tooltipRef}/>
        }
    />
}
